import { HTMLAttributes, useMemo } from 'react'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import HeroParallax from '@/components/base/hero-parallax'
import Media from '@/components/base/media'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { useLoaderStore } from '@/store/loader'

import { useHeroMotion } from './motion/hero'
import { cn } from '@/lib/utils'
import { WPMedia } from '@/types'

export type HeroProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    label: string
    title: string
    media: WPMedia
  }>

const Hero = ({ title, label, media, ...props }: HeroProps) => {
  const { motionRef } = useHeroMotion()
  const setLoaderState = useLoaderStore((state) => state.setState)
  const hasBackground = useMemo(
    () => media && (media.use_video ? media.video?.url : media.image?.url),
    [media]
  )

  const handleLoad = () => {
    setLoaderState({ pageLoaded: true, loaded: true })
  }

  return (
    <Section ref={motionRef} {...props}>
      <Grid>
        {hasBackground && media ? (
          <HeroParallax
            className={cn(
              'relative col-[1/-1] row-[1/-1] overflow-hidden',
              'after:absolute after:left-0 after:top-0 after:size-full after:bg-black/15'
            )}
          >
            <Media
              onLoad={handleLoad}
              className="object-cover"
              lazy={false}
              {...media}
              data-motion-media
            />
          </HeroParallax>
        ) : null}

        <Container
          className={cn(
            'relative col-[1/-1] row-[1/-1]',
            'pb-[4rem] pt-[17.2rem] md:pb-[8rem] md:pt-[22rem]'
          )}
        >
          <div className="grid gap-y-[1rem]">
            {label ? (
              <Typography
                variant="upper-small"
                paragraph
                className="font-bold uppercase text-white"
                data-motion-label
              >
                {label}
              </Typography>
            ) : null}

            {title ? (
              <Typography
                variant="hero-tb-customized"
                component="h1"
                className={cn('capitalize', 'font-light [&_strong]:font-medium', {
                  'text-white': hasBackground
                })}
                data-motion-title
              >
                {title}
              </Typography>
            ) : null}
          </div>
        </Container>
      </Grid>
    </Section>
  )
}

export default Hero
